import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  useTheme,
  Tooltip
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import SecurityIcon from '@mui/icons-material/Security';
import DevicesIcon from '@mui/icons-material/Devices';
import LogoutIcon from '@mui/icons-material/Logout';
import PaymentIcon from '@mui/icons-material/Payment';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import SubscriptionStatus from '../components/subscription/SubscriptionStatus';
import SubscriptionPlans from '../components/subscription/SubscriptionPlans';
import BillingHistory from '../components/subscription/BillingHistory';
import { createCheckoutSession, createCustomerPortalSession, getStripe } from '../services/stripeService';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { getCurrentUser } from '../services/cognitoService';
import { JoanProfile, getProfile } from '../services/joanApi';
import EmergencyContactsPanel from '../components/profile/EmergencyContactsPanel';
import DeviceIdDialog from '../components/profile/DeviceIdDialog';
import landingBackground from '../assets/backgrounds/landingBackground.avif';
import { CONTENT_WIDTH, GOLDEN_MINOR, PHI } from '../styles/golden';
import { BRAND, dialogPaperProps, frostedSx, glassCardSx, primaryButtonSx } from '../styles/ui';
import SiteHeader, { HomeButton } from '../components/layout/SiteHeader';
import SiteFooter from '../components/layout/SiteFooter';

// Interface for TabPanel props
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// TabPanel component
const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
};

const Profile: React.FC = () => {
  const { user, signOut, changePassword, isLoading: authLoading } = useAuth();
  const { 
    subscription, 
    transactions, 
    cancelSubscription, 
    createNewSubscription, 
    startTrial,
    refreshSubscription,
    refreshTransactions
  } = useSubscription();
  
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [tabValue, setTabValue] = React.useState<number>((location.state as { tab?: number } | null)?.tab ?? 0);
  const [showBillingHistory, setShowBillingHistory] = useState(false);
  const [showSubscriptionPlans, setShowSubscriptionPlans] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  
  // Account details, device and emergency contacts from the Joan web API
  const [profile, setProfile] = useState<JoanProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState<boolean>(true);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [deviceDialogOpen, setDeviceDialogOpen] = useState<boolean>(false);
  
  // Change password state
  const [openPasswordDialog, setOpenPasswordDialog] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [isPasswordLoading, setIsPasswordLoading] = useState<boolean>(false);
  const [lastPasswordChangeError, setLastPasswordChangeError] = useState<string | null>(null);
  const [lastPasswordChangeSuccess, setLastPasswordChangeSuccess] = useState<string | null>(null);
  const [lastPasswordChangeTime, setLastPasswordChangeTime] = useState<number | null>(null);
  // Force re-render counter
  const [renderCount, setRenderCount] = useState<number>(0);
  
  // Force update function
  const forceUpdate = () => {
    setRenderCount(prev => prev + 1);
  };

  // Helper function to format error messages
  const formatErrorMessage = (error: any): string => {
    if (!error) return 'An unknown error occurred';
    
    // Check for specific error types
    if (error.name === 'NotAuthorizedException' || error.code === 'NotAuthorizedException') {
      return 'Incorrect username or password';
    } else if (error.name === 'LimitExceededException' || error.code === 'LimitExceededException' || 
              (error.message && error.message.includes('Attempt limit exceeded'))) {
      return 'Too many attempts. Please try again after some time.';
    } else if (error.message) {
      return error.message;
    }
    
    return 'Failed to change password. Please try again.';
  };

  // Helper function to show browser notifications
  const showNotification = (title: string, message: string) => {
    if ('Notification' in window) {
      if (Notification.permission === 'granted') {
        new Notification(title, {
          body: message,
          icon: '/favicon.ico'
        });
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then(permission => {
          if (permission === 'granted') {
            new Notification(title, {
              body: message,
              icon: '/favicon.ico'
            });
          }
        });
      }
    }
  };

  // Get and refresh access token
  const getValidAccessToken = async (): Promise<string | null> => {
    try {
      // Try to get token from localStorage
      let token = localStorage.getItem('accessToken');
      
      if (!token) {
        console.error('No access token available');
        return null;
      }
      
      // Verify token is valid by making a test call
      try {
        await getCurrentUser(token);
        return token;
      } catch (err: any) {
        if (err.message && err.message.includes('expired')) {
          console.log('Token expired, user needs to log in again');
          // Clear the invalid token
          localStorage.removeItem('accessToken');
          return null;
        }
        throw err;
      }
    } catch (err) {
      console.error('Error getting valid access token:', err);
      return null;
    }
  };
  
  const loadProfile = async () => {
    setProfileLoading(true);
    setProfileError(null);
    try {
      setProfile(await getProfile());
    } catch (err: any) {
      console.error('Error loading profile:', err);
      setProfileError(err.message || 'Could not load your account details. Please try again.');
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);
  
  // Request notification permissions
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      // Wait a bit before requesting permission to avoid overwhelming the user
      const timer = setTimeout(() => {
        Notification.requestPermission();
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  // Check localStorage for password change status on component mount
  useEffect(() => {
    const storedError = localStorage.getItem('passwordChangeError');
    const storedSuccess = localStorage.getItem('passwordChangeSuccess');
    const storedTime = localStorage.getItem('passwordChangeTime');
    
    if (storedTime) {
      const time = parseInt(storedTime);
      setLastPasswordChangeTime(time);
      
      if (storedError) {
        console.log('Found stored error message:', storedError);
        setLastPasswordChangeError(storedError);
        setLastPasswordChangeSuccess(null);
      } else if (storedSuccess) {
        console.log('Found stored success message:', storedSuccess);
        setLastPasswordChangeSuccess(storedSuccess);
        setLastPasswordChangeError(null);
      }
      
      forceUpdate();
    }
  }, []);
  
  // Debug useEffect to log password change status
  useEffect(() => {
    console.log('Password change status:', { 
      error: lastPasswordChangeError,
      success: lastPasswordChangeSuccess,
      time: lastPasswordChangeTime,
      renderCount
    });
  }, [lastPasswordChangeError, lastPasswordChangeSuccess, lastPasswordChangeTime, renderCount]);

  // Clear password change status after 30 seconds
  useEffect(() => {
    if (lastPasswordChangeTime && (lastPasswordChangeError || lastPasswordChangeSuccess)) {
      const clearStatusTimer = setTimeout(() => {
        // Clear state
        setLastPasswordChangeError(null);
        setLastPasswordChangeSuccess(null);
        setLastPasswordChangeTime(null);
        
        // Clear localStorage
        localStorage.removeItem('passwordChangeError');
        localStorage.removeItem('passwordChangeSuccess');
        localStorage.removeItem('passwordChangeTime');
        
        console.log('Password change status cleared after timeout');
      }, 30000); // 30 seconds
      
      return () => clearTimeout(clearStatusTimer);
    }
  }, [lastPasswordChangeError, lastPasswordChangeSuccess, lastPasswordChangeTime]);
  

  // Check for success or canceled query params from Stripe Checkout
  React.useEffect(() => {
    const query = new URLSearchParams(location.search);
    const checkoutSuccess = query.get('checkout_success');
    const checkoutCanceled = query.get('checkout_canceled');
    
    if (checkoutSuccess === 'true') {
      // Refresh subscription data after successful checkout
      refreshSubscription();
      refreshTransactions();
      // Clear query params
      navigate('/profile', { replace: true });
    } else if (checkoutCanceled === 'true') {
      // Handle canceled checkout if needed
      navigate('/profile', { replace: true });
    }
  }, [location, navigate, refreshSubscription, refreshTransactions]);

  // Form validation schema for password change
  const passwordValidationSchema = Yup.object({
    currentPassword: Yup.string()
      .required('Current password is required'),
    newPassword: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
        'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
      )
      .required('New password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')], 'Passwords must match')
      .required('Confirm password is required')
  });

  // Formik setup for password change
  const passwordFormik = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    },
    validationSchema: passwordValidationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setIsPasswordLoading(true);
      setPasswordError(null);
      setPasswordSuccess(null);
      
      try {
        // Verify we have a valid access token
        const token = await getValidAccessToken();
        if (!token) {
          throw new Error('Your session has expired. Please log in again.');
        }
        
        // Call the changePassword function from AuthContext
        await changePassword(values.currentPassword, values.newPassword);
        
        // Show success message and reset form
        const successMessage = 'Password changed successfully';
        setPasswordSuccess(successMessage);
        resetForm();
        
        // Store success message for display in the profile
        // Clear any previous error
        setLastPasswordChangeError(null);
        setLastPasswordChangeSuccess(successMessage);
        setLastPasswordChangeTime(Date.now());
        
        // Use localStorage to ensure the success persists across renders
        localStorage.removeItem('passwordChangeError'); // Clear any previous error
        localStorage.setItem('passwordChangeSuccess', successMessage);
        localStorage.setItem('passwordChangeTime', Date.now().toString());
        
        // Force a re-render to ensure the success message is displayed
        forceUpdate();
        
        // Display browser notification for success
        showNotification('Password Changed', 'Your password has been changed successfully.');
        
        // Close the dialog after a short delay
        setTimeout(() => {
          setOpenPasswordDialog(false);
          setPasswordSuccess(null);
        }, 2000);
      } catch (err: any) {
        console.error('Error changing password:', err);
        const errorMessage = formatErrorMessage(err);
        setPasswordError(errorMessage);
        
        // Store the error message and timestamp for display in the profile
        console.log('Setting error message:', errorMessage);
        
        // Use localStorage to ensure the error persists across renders
        localStorage.setItem('passwordChangeError', errorMessage);
        localStorage.setItem('passwordChangeTime', Date.now().toString());
        
        // Set the error state
        setLastPasswordChangeError(errorMessage);
        setLastPasswordChangeTime(Date.now());
        
        // Force a re-render immediately
        forceUpdate();
        
        // Force another re-render after the dialog closes
        setTimeout(() => {
          // Get the error from localStorage to ensure it persists
          const storedError = localStorage.getItem('passwordChangeError');
          if (storedError) {
            setLastPasswordChangeError(storedError);
            setLastPasswordChangeTime(parseInt(localStorage.getItem('passwordChangeTime') || '0'));
            forceUpdate();
            console.log('Error message restored from localStorage:', storedError);
          }
        }, 2500); // Give more time to ensure dialog is fully closed
        
        // Display browser notification for error
        showNotification('Password Change Failed', errorMessage);
        
        // Close the dialog after a short delay to show the error in the profile
        setTimeout(() => {
          setOpenPasswordDialog(false);
        }, 2000);
      } finally {
        setIsPasswordLoading(false);
        setSubmitting(false);
      }
    }
  });

  // Handle opening the password dialog
  const handleOpenPasswordDialog = () => {
    setOpenPasswordDialog(true);
    setPasswordError(null);
    setPasswordSuccess(null);
    passwordFormik.resetForm();
    // We don't clear lastPasswordChangeError here so it remains visible in the UI
  };

  // Handle closing the password dialog
  const handleClosePasswordDialog = () => {
    setOpenPasswordDialog(false);
    passwordFormik.resetForm();
    
    // Don't clear error state when closing the dialog
    // This ensures the error message remains visible in the UI
    console.log('Dialog closed, preserving error state:', lastPasswordChangeError);
    
    // Force a re-render after closing the dialog
    if (lastPasswordChangeError) {
      setTimeout(() => {
        forceUpdate();
        console.log('Forced update after dialog close');
      }, 100);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleManageSubscription = async () => {
    if (subscription) {
      // If user already has a subscription, redirect to Stripe Customer Portal
      setPortalLoading(true);
      try {
        if (user) {
          try {
            const portalUrl = await createCustomerPortalSession(
              user.username,
              `${window.location.origin}/profile`
            );
            window.location.href = portalUrl;
          } catch (portalError: any) {
            // Handle 404 errors (customer not found)
            if (portalError.response && portalError.response.status === 404) {
              // Refresh subscription data since customer might not exist
              await refreshSubscription();
              alert("Subscription information outdated. We've refreshed your subscription information. Please try again.");
            } else {
              // Handle other errors
              alert(portalError.message || "Error accessing subscription management. Please try again later.");
            }
          }
        }
      } catch (error) {
        alert("Could not access subscription management. Please try again later.");
      } finally {
        setPortalLoading(false);
      }
    } else {
      // If no subscription, show subscription plans
      setShowSubscriptionPlans(true);
    }
  };

  const handleViewBillingHistory = () => {
    setShowBillingHistory(true);
  };

  const handleCancelSubscription = () => {
    setShowCancelDialog(true);
  };

  const handleConfirmCancel = async (cancelImmediately: boolean = false) => {
    await cancelSubscription(cancelImmediately);
    setShowCancelDialog(false);
  };

  const handleSelectPlan = async (plan: any) => {
    if (plan.interval === 'trial') {
      await startTrial();
      setShowSubscriptionPlans(false);
    } else {
      // Create Stripe Checkout session
      setCheckoutLoading(true);
      try {
        if (user) {
          const sessionId = await createCheckoutSession(
            user.username,
            plan.id,
            `${window.location.origin}/profile?checkout_success=true`,
            `${window.location.origin}/profile?checkout_canceled=true`,
            user
          );
          
          // Redirect to Stripe Checkout
          const stripe = await getStripe();
          if (stripe) {
            await stripe.redirectToCheckout({ sessionId });
          }
        }
      } catch (error) {
        console.error('Error redirecting to checkout:', error);
      } finally {
        setCheckoutLoading(false);
      }
    }
  };

  const navItems = [
    { label: 'Personal Info', shortLabel: 'Personal', icon: <PersonIcon fontSize="small" /> },
    { label: 'Emergency Contacts', shortLabel: 'Contacts', icon: <ContactPhoneIcon fontSize="small" /> },
    { label: 'Subscription', shortLabel: 'Subscription', icon: <PaymentIcon fontSize="small" /> },
  ];

  // Account status reflects whether a device is linked (alerts need one)
  const status = profileLoading
    ? { color: BRAND.faint, label: 'Checking…' }
    : profile?.deviceStatus === 'linked'
      ? { color: BRAND.accent, label: 'Device linked' }
      : profile?.deviceStatus === 'shared'
        ? { color: BRAND.warning, label: 'Contact support' }
        : profile
          ? { color: BRAND.warning, label: 'No device linked' }
          : { color: BRAND.faint, label: 'Unavailable' };

  const statusDot = (
    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: status.color, flexShrink: 0 }} />
  );

  const detailLabelSx = { color: BRAND.muted, mb: 0.5 };
  const detailValueSx = { color: BRAND.ink, overflowWrap: 'anywhere' as const };

  const sectionTitle = (title: string) => (
    <Typography variant="h4" component="h1" sx={{ color: BRAND.ink, mb: { xs: 3, md: 4 } }}>
      {title}
    </Typography>
  );

  return (
    <>
      <Box
        sx={{
          minHeight: '100vh',
          '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
          backgroundColor: 'var(--joan-bg)',
          backgroundImage: {
            xs: `linear-gradient(${BRAND.veilFull}, ${BRAND.veilFull}), url(${landingBackground})`,
            md: `linear-gradient(to right, ${BRAND.veilStrong} ${GOLDEN_MINOR}, ${BRAND.veilSoft} 100%), url(${landingBackground})`
          },
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: { md: 'fixed' },
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <SiteHeader
          actions={
            <>
              <HomeButton iconOnlyOnPhone />
              <Button
                onClick={handleSignOut}
                disabled={authLoading}
                startIcon={<LogoutIcon />}
                aria-label="Sign out"
                sx={{
                  color: BRAND.ink,
                  minWidth: { xs: 44, sm: 64 },
                  px: { xs: 1, sm: 2 },
                  '&:hover': { backgroundColor: BRAND.hover },
                  '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 }, ml: { xs: 0, sm: -0.5 } }
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Sign out</Box>
              </Button>
            </>
          }
        />

        {/* Phone and tablet: section tabs pinned under the header */}
        <Box
          sx={{
            display: { md: 'none' },
            position: 'sticky',
            top: { xs: 'calc(56px + env(safe-area-inset-top))', sm: 'calc(64px + env(safe-area-inset-top))' },
            zIndex: theme.zIndex.appBar - 1,
            ...frostedSx,
            backgroundColor: BRAND.header,
            borderBottom: `1px solid ${BRAND.line}`
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            aria-label="Profile sections"
            TabIndicatorProps={{ sx: { backgroundColor: BRAND.pink, height: 3 } }}
          >
            {navItems.map((item, index) => (
              <Tab
                key={item.label}
                id={`profile-tab-${index}`}
                aria-controls={`profile-tabpanel-${index}`}
                icon={item.icon}
                iconPosition="start"
                label={item.shortLabel}
                sx={{
                  minHeight: 55,
                  px: 1,
                  gap: 0.5,
                  fontSize: '0.8rem',
                  textTransform: 'none',
                  color: BRAND.muted,
                  '& .MuiTab-iconWrapper': { display: { xs: 'none', sm: 'inline-flex' } },
                  '&.Mui-selected': { color: BRAND.ink }
                }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Desktop: sidebar and content in a 1 : φ² split (27.6% / 72.4%) */}
        <Box
          sx={{
            flex: 1,
            display: { md: 'grid' },
            gridTemplateColumns: { md: `minmax(260px, 1fr) minmax(0, ${(PHI * PHI).toFixed(3)}fr)` }
          }}
        >
        {/* Desktop sidebar */}
        <Box
          component="nav"
          aria-label="Profile navigation"
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            position: 'sticky',
            top: 64,
            height: 'calc(100vh - 64px)',
            overflowY: 'auto',
            p: { md: 4, lg: 5 },
            ...frostedSx,
            backgroundColor: BRAND.panel,
            borderRight: `1px solid ${BRAND.line}`
          }}
        >
          <Typography variant="h4" component="p" sx={{ color: BRAND.ink, mb: 1 }}>
            Profile
          </Typography>
          {user?.fullName && (
            <Typography variant="body2" sx={{ color: BRAND.muted, mb: 4, overflowWrap: 'anywhere' }}>
              Signed in as {user.fullName}
            </Typography>
          )}

          <List sx={{ p: 0 }}>
            {navItems.map((item, index) => (
              <ListItem key={item.label} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  selected={tabValue === index}
                  onClick={() => setTabValue(index)}
                  sx={{
                    py: 1.5,
                    borderRadius: 1,
                    '&.Mui-selected': { backgroundColor: BRAND.selected },
                    '&.Mui-selected:hover, &:hover': { backgroundColor: BRAND.hover }
                  }}
                >
                  <ListItemIcon sx={{ color: tabValue === index ? BRAND.pink : BRAND.muted, minWidth: 34 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ sx: { color: BRAND.ink, fontSize: '0.95rem', fontWeight: tabValue === index ? 600 : 400 } }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <Box sx={{ mt: 'auto', pt: 4 }}>
            <Box sx={{ backgroundColor: BRAND.card, border: `1px solid ${BRAND.line}`, borderRadius: '13px', p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ color: BRAND.muted }}>
                  Status
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {statusDot}
                  <Typography variant="body2" sx={{ color: BRAND.ink }}>
                    {status.label}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Main content */}
        <Box component="main" sx={{ minWidth: 0, px: { xs: 3, sm: 4, md: 5, lg: 6 }, py: { xs: 4, md: 6 }, pb: 'calc(34px + env(safe-area-inset-bottom))' }}>
          <Box sx={{ maxWidth: CONTENT_WIDTH.page, mx: 'auto' }}>
            <TabPanel value={tabValue} index={0}>
              {sectionTitle('Personal Information')}

              <Box component="section" sx={glassCardSx}>
                <Typography variant="h6" component="h2" sx={{ color: BRAND.ink, mb: 3 }}>
                  User Details
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, columnGap: 4, rowGap: 3 }}>
                  <Box>
                    <Typography variant="body2" sx={detailLabelSx}>
                      Full Name
                    </Typography>
                    <Typography variant="body1" sx={detailValueSx}>
                      {user?.fullName || 'Not provided'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={detailLabelSx}>
                      Email
                    </Typography>
                    <Typography variant="body1" sx={detailValueSx}>
                      {user?.email || 'Not provided'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={detailLabelSx}>
                      Phone Number
                    </Typography>
                    <Typography variant="body1" sx={detailValueSx}>
                      {user?.phoneNumber || 'Not provided'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={detailLabelSx}>
                      Device ID
                    </Typography>
                    <Typography variant="body1" sx={{ ...detailValueSx, display: 'flex', alignItems: 'center' }}>
                      <DevicesIcon sx={{ mr: 1, fontSize: '1rem' }} />
                      {profileLoading ? 'Loading…' : profileError ? 'Unavailable' : profile?.deviceId || 'Not linked'}
                    </Typography>
                    {profile?.deviceStatus === 'none' && (
                      <Button
                        size="small"
                        onClick={() => setDeviceDialogOpen(true)}
                        sx={{ mt: 0.5, ml: -1, px: 1, color: BRAND.pink }}
                      >
                        Add device ID
                      </Button>
                    )}
                    {profile?.deviceStatus === 'shared' && (
                      <Typography variant="body2" sx={{ color: BRAND.warning, mt: 0.5 }}>
                        Linked to more than one account. Please <Link component={RouterLink} to="/contact" sx={{ color: 'inherit', fontWeight: 600 }}>contact support</Link>.
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Security Section */}
                <Box sx={{ mt: 4, pt: 4, borderTop: `1px solid ${BRAND.line}`, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {lastPasswordChangeSuccess && (
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: BRAND.accentSoft,
                      p: 2,
                      borderRadius: 1,
                      border: `1px solid ${BRAND.line}`
                    }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: BRAND.accent, mr: 1.5, flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ color: BRAND.accent, fontWeight: 500 }}>
                        {lastPasswordChangeSuccess}
                      </Typography>
                    </Box>
                  )}

                  {lastPasswordChangeError && (
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: BRAND.warningSoft,
                      p: 2,
                      borderRadius: 1,
                      border: `1px solid ${BRAND.line}`
                    }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: BRAND.warning, mr: 1.5, flexShrink: 0 }} />
                      <Tooltip title={lastPasswordChangeError} placement="top">
                        <Typography variant="body2" sx={{ color: BRAND.warning, fontWeight: 500 }}>
                          {lastPasswordChangeError}
                        </Typography>
                      </Tooltip>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      onClick={handleOpenPasswordDialog}
                      startIcon={<SecurityIcon />}
                      sx={{ ...primaryButtonSx, width: { xs: '100%', sm: 'auto' } }}
                    >
                      Change Password
                    </Button>
                  </Box>
                </Box>
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              {sectionTitle('Emergency Contacts')}

              <Box component="section" sx={glassCardSx}>
                <EmergencyContactsPanel
                  profile={profile}
                  loading={profileLoading}
                  loadError={profileError}
                  onRetry={loadProfile}
                  onChange={setProfile}
                  onAddDevice={() => setDeviceDialogOpen(true)}
                />
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              {sectionTitle('Subscription')}

              <Box component="section" sx={glassCardSx}>
                {showSubscriptionPlans ? (
                  <SubscriptionPlans
                    onSelectPlan={handleSelectPlan}
                    currentPlanId={subscription?.planId}
                  />
                ) : showBillingHistory ? (
                  <BillingHistory
                    transactions={transactions}
                    onClose={() => setShowBillingHistory(false)}
                  />
                ) : (
                  <SubscriptionStatus
                    subscription={subscription}
                    onManageSubscription={handleManageSubscription}
                    onCancelSubscription={handleCancelSubscription}
                    onViewBillingHistory={handleViewBillingHistory}
                  />
                )}
              </Box>
            </TabPanel>
          </Box>
        </Box>
        </Box>
        <SiteFooter />
      </Box>

      <DeviceIdDialog
        open={deviceDialogOpen}
        onClose={() => setDeviceDialogOpen(false)}
        onLinked={(updated) => {
          setProfile(updated);
          setDeviceDialogOpen(false);
        }}
      />

      {/* Cancel Subscription Dialog */}
      <Dialog
        open={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        PaperProps={dialogPaperProps}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" component="h2" gutterBottom sx={{ color: BRAND.ink }}>
            Cancel Subscription
          </Typography>
          <Typography variant="body1" paragraph sx={{ color: BRAND.muted }}>
            Are you sure you want to cancel your subscription?
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', gap: 2, mt: 3 }}>
            <Button 
              variant="outlined" 
              onClick={() => setShowCancelDialog(false)}
              sx={{ 
                color: BRAND.ink,
                borderColor: BRAND.lineStrong,
                '&:hover': { borderColor: BRAND.ink, backgroundColor: BRAND.hover }
              }}
            >
              Keep Subscription
            </Button>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
              <Button 
                variant="outlined" 
                color="error"
                onClick={() => handleConfirmCancel(false)}
              >
                Cancel at Period End
              </Button>
              <Button 
                variant="contained" 
                color="error"
                onClick={() => handleConfirmCancel(true)}
              >
                Cancel Now
              </Button>
            </Box>
          </Box>
        </Box>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog
        open={openPasswordDialog}
        onClose={handleClosePasswordDialog}
        PaperProps={dialogPaperProps}
      >
        <DialogTitle sx={{ color: BRAND.ink }}>
          Change Password
        </DialogTitle>
        <form onSubmit={passwordFormik.handleSubmit}>
          <DialogContent>
            {passwordError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPasswordError(null)}>
                {passwordError}
              </Alert>
            )}
            
            {passwordSuccess && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPasswordSuccess(null)}>
                {passwordSuccess}
              </Alert>
            )}
            
            <TextField
              autoFocus
              margin="dense"
              id="currentPassword"
              name="currentPassword"
              label="Current Password"
              type="password"
              fullWidth
              variant="outlined"
              value={passwordFormik.values.currentPassword}
              onChange={passwordFormik.handleChange}
              error={passwordFormik.touched.currentPassword && Boolean(passwordFormik.errors.currentPassword)}
              helperText={passwordFormik.touched.currentPassword && passwordFormik.errors.currentPassword}
              disabled={isPasswordLoading}
              sx={{
                mb: 2,
              }}
            />
            
            <TextField
              margin="dense"
              id="newPassword"
              name="newPassword"
              label="New Password"
              type="password"
              fullWidth
              variant="outlined"
              value={passwordFormik.values.newPassword}
              onChange={passwordFormik.handleChange}
              error={passwordFormik.touched.newPassword && Boolean(passwordFormik.errors.newPassword)}
              helperText={passwordFormik.touched.newPassword && passwordFormik.errors.newPassword}
              disabled={isPasswordLoading}
              sx={{
                mb: 2,
              }}
            />
            
            <TextField
              margin="dense"
              id="confirmPassword"
              name="confirmPassword"
              label="Confirm New Password"
              type="password"
              fullWidth
              variant="outlined"
              value={passwordFormik.values.confirmPassword}
              onChange={passwordFormik.handleChange}
              error={passwordFormik.touched.confirmPassword && Boolean(passwordFormik.errors.confirmPassword)}
              helperText={passwordFormik.touched.confirmPassword && passwordFormik.errors.confirmPassword}
              disabled={isPasswordLoading}
              sx={{
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button 
              onClick={handleClosePasswordDialog} 
              disabled={isPasswordLoading}
              sx={{ 
                color: BRAND.ink,
                '&:hover': { backgroundColor: BRAND.hover }
              }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={isPasswordLoading}
              sx={primaryButtonSx}
            >
              {isPasswordLoading ? <CircularProgress size={24} color="inherit" /> : 'Change Password'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Loading Dialog for Checkout/Portal */}
      <Dialog 
        open={checkoutLoading || portalLoading}
        PaperProps={dialogPaperProps}
      >
        <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography sx={{ color: BRAND.ink }}>
            {checkoutLoading ? 'Redirecting to checkout...' : 'Opening customer portal...'}
          </Typography>
        </Box>
      </Dialog>
    </>
  );
};

export default Profile;
